import { defineStore } from "pinia"
import { date } from "quasar"
import { useAuthStore } from "src/stores/authStore"
import { useConfigurationStore } from "src/stores/configurationStore"
import { Log } from "src/util/classes/log"
import { DATE_TIME_FULL_MASK } from "src/util/constants/configurations"
import { getCurrentUser, patchCurrentUser } from "src/util/http/springboard/user"
import {
  UserCurrent,
  UserPermissionAccess,
  UserPermissionType,
} from "src/util/types/dataModels/user"

export interface UserStoreType extends UserCurrent {
  profilePicture?: string
  helpDialogShown: boolean
  // Dedicated variable so that we can persist it, since apiCallStore is not persistent
  latestAPICall?: number
}

export const useUserStore = defineStore("pinia-userStore", {
  persist: true,

  state: (): UserStoreType => ({
    id: "",
    email: "",
    first_name: "",
    last_name: "",
    is_active: false,
    is_superuser: false,
    roles: [],
    settings: null,
    profilePicture: "",
    helpDialogShown: false,
    latestAPICall: undefined,
    permissions: {},
  }),

  getters: {
    getFullName(state: UserStoreType): string {
      return (state.first_name + " " + state.last_name).trim()
    },

    getLatestCallTime(state: UserStoreType): string {
      return date.formatDate(state.latestAPICall, DATE_TIME_FULL_MASK)
    },

    getUserData(state: UserStoreType): Omit<UserCurrent, "settings"> {
      return {
        id: state.id,
        email: state.email,
        first_name: state.first_name,
        last_name: state.last_name,
        is_active: state.is_active,
        is_superuser: state.is_superuser,
        roles: state.roles,
        permissions: state.permissions,
      }
    },
  },

  actions: {
    updateUserData(user: UserCurrent) {
      this.id = user.id
      this.email = user.email
      this.first_name = user.first_name
      this.last_name = user.last_name
      this.is_active = user.is_active
      this.is_superuser = user.is_superuser
      this.roles = user.roles
      this.permissions = user.permissions

      const configurationStore = useConfigurationStore()

      user.settings
        ? configurationStore.updateConfigurations(user.settings.data)
        : configurationStore.$reset()
    },

    async fetchUserData() {
      try {
        // fetching user details from the server
        const user = await getCurrentUser()
        this.updateUserData(user)
      } catch (error) {
        Log.error({
          message: "An error occurred while trying to get the user details",
          error,
        })
      }
    },

    async uploadUserData(userData: Partial<UserCurrent>) {
      try {
        const updatedUser = await patchCurrentUser({}, userData)
        this.updateUserData(updatedUser)
      } catch (error) {
        Log.error({
          message: "An error occurred while trying to upload the user details",
          error,
        })
      }
    },

    checkUserPermission(
      permissionType: UserPermissionType,
      permissionAccess: UserPermissionAccess | UserPermissionAccess[],
      permissionSpecifics?: { workspaceUUID?: string; workDomainUUID?: string }
    ) {
      const workDomainKey = permissionSpecifics?.workDomainUUID
      let workspaceKey = permissionSpecifics?.workspaceUUID

      if (workspaceKey === undefined) {
        const authStore = useAuthStore()
        workspaceKey = authStore.selectedWorkspace?.id
      }

      let hasAccess = true

      for (const accessLevel of permissionAccess) {
        // Check to see if the permission access is available in the global permissions
        // If it is, no need to check further as that supersedes any workspace and work domain permissions
        if (
          this.permissions.global &&
          this.permissions.global[permissionType]?.includes(accessLevel)
        ) {
          continue
        }

        // Not possible to get permissions for a workspace without a valid workspace UUID, ergo, you don't have access
        if (workspaceKey === undefined || this.permissions[workspaceKey] === undefined) return false

        const workspaceData = this.permissions[workspaceKey]

        // Check to see if the permission access is available in the workspace permissions
        // If it is, no need to check further as that supersedes any work domain permissions
        if (workspaceData.workspace_permissions[permissionType]?.includes(accessLevel)) {
          continue
        }

        if (workDomainKey) {
          const workDomainPermissions = workspaceData.work_domain_permissions[workDomainKey]

          // Check if the work domain we specified has the appropriate permission access
          if (workDomainPermissions[permissionType]?.includes(accessLevel)) {
            continue
          }
        }

        // User doesn't have global, workspace or work domain specific permission
        hasAccess = false
        break
      }

      return hasAccess
    },
    /**
     * Returns "allWorkspacePermissions" if the user has global permissions
     * else
     * returns the list of work domains which the user have permissions
     *
     * @param permissionType
     * @param permissionAccess
     */
    getWorkDomainsWithPermission(
      permissionType: UserPermissionType,
      permissionAccess: UserPermissionAccess | UserPermissionAccess[]
    ): "allWorkspacePermissions" | string[] {
      const authStore = useAuthStore()
      const workspaceKey = authStore.selectedWorkspace?.id

      // Not possible to get permissions for a workspace without a valid workspace UUID, ergo, you don't have access
      if (workspaceKey === undefined || this.permissions[workspaceKey] === undefined) return []

      const workspaceData = this.permissions[workspaceKey]
      const workDomainsWithPermissions = []

      for (const accessLevel of permissionAccess) {
        // Check to see if the permission access is available in the global workspace permissions
        // If it is, no need to check further as that supersedes any work domain permissions
        if (workspaceData.workspace_permissions[permissionType]?.includes(accessLevel)) {
          return "allWorkspacePermissions"
        } else {
          const workDomainsInWorkSpace = Object.keys(workspaceData.work_domain_permissions)

          for (const workDomain of workDomainsInWorkSpace) {
            const workDomainPermissions = workspaceData.work_domain_permissions[workDomain]

            // Check if the work domain has the appropriate permission access
            if (workDomainPermissions[permissionType]?.includes(accessLevel)) {
              workDomainsWithPermissions.push(workDomain)
            }
          }
        }
      }
      return workDomainsWithPermissions
    },
  },
})
